import React, { useMemo, useState } from 'react';

import { Checkbox } from 'antd-v5';
import { CheckboxChangeEvent } from 'antd-v5/lib';
import { difference, get, intersection, lowerCase, orderBy } from 'lodash';
import { MdOutlineContentPaste, MdSearch } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button, Text } from 'scouting-ui/lib';

import { useGetLookupPersonPositionsQuery } from '@modules/lookups/api';
import {
  PERMISSIONS_GUIDE_REPORT_URL,
  PERMISSION_CATEGORIES,
} from '@modules/permissionManager/constants';
import { useUnitRosterAdultsWithPositions } from '@modules/permissionManager/duck/hooks';
import {
  Input,
  PersonAvatar,
  S,
  Table,
  TableMobile,
} from '@modules/shared/components';
import { unitTypeIdForUnitTypeName } from '@shared/constants';
import {
  isMobileSel,
  selectedOrganizationSel,
} from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';

import styles from '../../permissionsTab.less';
import {
  RosterAdultMemberExtended,
  RosterYouthMemberExtended,
  getRequiredPermissionNames,
} from '../../utils';
import { PermissionItem } from './PermissionItem';

export const PermissionsViewPanel = () => {
  const isMobile = useSelector(isMobileSel);
  const { unitTypeId } = useSelector(selectedOrganizationSel);
  const [searchTxt, setSearchTxt] = useState<string>('');
  const [showUnitSub, setShowUnitSub] = useState<{
    unit: boolean;
    subUnit: boolean;
  }>({
    unit: true,
    subUnit: true,
  });

  const { adultsData, loading: loadingPositions } =
    useUnitRosterAdultsWithPositions();
  const { data: personPositionsLookup, isFetching: loadingLookUp } =
    useGetLookupPersonPositionsQuery();
  const loading = loadingPositions || loadingLookUp;
  const unitTypePositionsFiltered = useMemo(() => {
    const programKey =
      unitTypeId === 6 ? 'explorer' : unitTypeIdForUnitTypeName[unitTypeId];

    return personPositionsLookup
      ?.filter(item => get(item, `${programKey}Position`, false))
      .map(({ name }) => name);
  }, [personPositionsLookup, unitTypeId]);

  const handleChange = (e: CheckboxChangeEvent) => {
    const { name } = e.target;
    const newValues = {
      ...showUnitSub,
      [name as 'unit' | 'subUnit']: !showUnitSub[name as 'unit' | 'subUnit'],
    };

    setShowUnitSub(newValues);
  };

  const resultFilteredData = useMemo(() => {
    const filteredData = searchTxt
      ? adultsData.filter(({ personFullName, nickName, lastName }) =>
          lowerCase(
            nickName ? `${nickName} ${lastName}` : personFullName,
          ).includes(lowerCase(searchTxt)),
        )
      : adultsData;

    return orderBy(
      filteredData,
      [
        person => lowerCase(person.lastName),
        person => lowerCase(person.firstName),
      ],
      ['asc', 'asc'],
    );
  }, [adultsData, searchTxt]);

  const unitRequiredRosterPositions = useMemo(() => {
    if (!showUnitSub.unit) return {};
    return resultFilteredData
      .filter(person => !person?.isExpired)
      .reduce((curr, person) => {
        const permissions = getRequiredPermissionNames(
          [...person.positions, ...person.registrationData],
          'unit',
        ).filter(position => unitTypePositionsFiltered?.includes(position));

        return { ...curr, [person.personGuid]: permissions };
      }, {} as Record<string, string[]>);
  }, [resultFilteredData, showUnitSub.unit, unitTypePositionsFiltered]);

  const rosterAllPositions = useMemo(() => {
    if (!showUnitSub.unit) return {};
    return resultFilteredData
      .filter(person => !person?.isExpired)
      .reduce(
        (curr, person) => ({
          ...curr,
          [person.personGuid]: person.allPositions,
        }),
        {} as Record<string, string[]>,
      );
  }, [resultFilteredData, showUnitSub.unit]);

  const subUnitRequiredRosterPositions = useMemo(() => {
    if (!showUnitSub.subUnit) return {};

    return resultFilteredData
      .filter(person => !person?.isExpired)
      .reduce((curr, person) => {
        const permissions = getRequiredPermissionNames(
          [...person.positions, ...person.registrationData],
          'subUnit',
        ).filter(position => unitTypePositionsFiltered?.includes(position));

        // Positions records superposes subUnit, ex. Profile Editor SubUnit -> Profile Editor General
        const difer = difference(
          permissions,
          rosterAllPositions[person.personGuid],
        );
        return { ...curr, [person.personGuid]: difer };
      }, {} as Record<string, string[]>);
  }, [
    resultFilteredData,
    rosterAllPositions,
    showUnitSub.subUnit,
    unitTypePositionsFiltered,
  ]);

  const getColumns = () =>
    PERMISSION_CATEGORIES.map(category => ({
      title: category.title,
      key: category.key,
      render: (
        _value: unknown,
        info: RosterAdultMemberExtended | RosterYouthMemberExtended,
      ) => {
        const unitRequiredPositionsRecord =
          unitRequiredRosterPositions[info.personGuid] ?? [];
        const positionsRecord = rosterAllPositions[info.personGuid] ?? [];
        const subUnitRequiredPositionsRecord =
          subUnitRequiredRosterPositions[info.personGuid] ?? [];

        return (
          <div className={styles.permissionCell}>
            {intersection(category.positions, [
              ...positionsRecord,
              ...subUnitRequiredPositionsRecord,
              ...unitRequiredPositionsRecord,
            ]).map(permissionKey => (
              <PermissionItem
                key={`${info.personGuid}-${permissionKey}`}
                label={permissionKey}
                scope={
                  subUnitRequiredPositionsRecord.includes(permissionKey)
                    ? 'subUnit'
                    : 'unit'
                }
              />
            ))}
          </div>
        );
      },
    }));

  const columns = [
    {
      title: intl.formatMessage({
        id: 'shared.name',
      }),
      dataIndex: 'personFullName',
      key: 'personFullName',
      width: '20%',
      render(
        personFullName: string,
        {
          isAdult,
          pictureUrl,
          nickName,
          lastName,
          gender,
        }: RosterAdultMemberExtended | RosterYouthMemberExtended,
      ) {
        return (
          <div className={styles.nameContainer}>
            <PersonAvatar
              isAdult={isAdult}
              gender={gender}
              pictureUrl={pictureUrl}
              bordered={false}
              className={styles.avatar}
            />
            <S colored size="4">
              {nickName ? `${nickName} ${lastName}` : personFullName}
            </S>
          </div>
        );
      },
    },
    ...getColumns(),
  ];

  const getNameRow = (
    item: RosterAdultMemberExtended | RosterYouthMemberExtended,
  ) => {
    const { personFullName, isAdult, pictureUrl, nickName, lastName, gender } =
      item;

    return {
      rowId: 'personFullName',
      label: (
        <S size="4" bold>
          {intl.formatMessage({ id: 'shared.name' })}
        </S>
      ),
      content: (
        <div className={styles.nameContainer}>
          <PersonAvatar
            isAdult={isAdult}
            gender={gender}
            pictureUrl={pictureUrl}
            bordered={false}
            className={styles.avatar}
          />
          <S colored size="4">
            {nickName ? `${nickName} ${lastName}` : personFullName}
          </S>
        </div>
      ),
    };
  };

  const getMobileColumns = (
    item: RosterAdultMemberExtended | RosterYouthMemberExtended,
  ) =>
    PERMISSION_CATEGORIES.map(category => {
      const unitRequiredPositionsRecord =
        unitRequiredRosterPositions[item.personGuid] ?? [];
      const positionsRecord = rosterAllPositions[item.personGuid] ?? [];
      const subUnitRequiredPositionsRecord =
        subUnitRequiredRosterPositions[item.personGuid] ?? [];

      return {
        rowId: category.key,
        label: (
          <S size="4" bold>
            {category.title}
          </S>
        ),
        content: (
          <div className={styles.permissionCell}>
            {intersection(category.positions, [
              ...positionsRecord,
              ...subUnitRequiredPositionsRecord,
              ...unitRequiredPositionsRecord,
            ]).map(permissionKey => (
              <PermissionItem
                key={`${item.personGuid}-${permissionKey}`}
                label={permissionKey}
                scope={
                  subUnitRequiredPositionsRecord.includes(permissionKey)
                    ? 'subUnit'
                    : 'unit'
                }
              />
            ))}
          </div>
        ),
      };
    });

  const getMobileRows = (
    item: RosterAdultMemberExtended | RosterYouthMemberExtended,
  ) => [getNameRow(item), ...getMobileColumns(item)];

  const getPersonRowKey = (rowData: RosterAdultMemberExtended) => {
    const { userId } = rowData;
    return `${userId}`;
  };

  const handleOpenPermissionsGuide = () => {
    window.open(PERMISSIONS_GUIDE_REPORT_URL, '_blank', 'noopener,noreferrer');
  };

  const renderTitle = () => (
    <div className={styles.tableTitleViewer}>
      <Button
        size="small"
        type="link"
        icon={<MdOutlineContentPaste />}
        className={styles.report}
        onClick={handleOpenPermissionsGuide}
      >
        <FormattedMessage id="permissionManager.viewPermissions.report" />
      </Button>
      <Checkbox onChange={handleChange} checked={showUnitSub.unit} name="unit">
        <div className={styles.checkboxDotContainer}>
          <span className={styles.unitDot} />
          <Text font="body-body-2" color="color-white">
            Unit
          </Text>
        </div>
      </Checkbox>
      <Checkbox
        onChange={handleChange}
        checked={showUnitSub.subUnit}
        name="subUnit"
      >
        <div className={styles.checkboxDotContainer}>
          <span className={styles.subUnitDot} />
          <Text font="body-body-2" color="color-white">
            Sub-unit
          </Text>
        </div>
      </Checkbox>
      <Input
        size="large"
        rounded
        addonAfter={<MdSearch />}
        className={styles.searchInput}
        placeholder="Search by Name"
        value={searchTxt}
        onChange={e => setSearchTxt(e.target.value)}
      />
    </div>
  );

  if (isMobile) {
    return (
      <div className={styles.mobilePanelContainer}>
        <div className={styles.searchInputContainer}>
          <Input
            size="large"
            rounded
            addonAfter={<MdSearch />}
            className={styles.searchInput}
            placeholder="Search by Name"
            value={searchTxt}
            onChange={e => setSearchTxt(e.target.value)}
          />
        </div>
        <TableMobile
          rowKey={getPersonRowKey}
          rows={getMobileRows}
          loading={loading}
          items={resultFilteredData}
        />
      </div>
    );
  }

  return (
    <Table
      rowKey={getPersonRowKey}
      loading={loading}
      title={renderTitle}
      columns={columns}
      dataSource={resultFilteredData}
      size="middle"
      scroll={{ x: true }}
      pagination={resultFilteredData.length > 10}
      className={styles.tableHeader}
    />
  );
};
